import { chunkArray } from '@brand/utils';
import { Column, Header, Spacer, Typo } from 'components/ui-kit';
import { useFormikContext } from 'formik';
import { cartService, DeliveryTypeCode, ShippingType } from 'modules/cart';
import { useEffect, useMemo, useState } from 'react';
import { DeliveryTypeCartField } from './components';
import styles from './delivery-type-form.module.scss';
import {
  EKATERINBURG,
  FREE_DELIVERY_PRICE_TRIGGER,
  RUSSIA_CODE,
  SVERDLOVSKAYA_OBLAST,
} from '../../offer-form.consts';
import { TOfferForm } from '../../offer-form.types';
import { useDeliveryTypeContext } from '../delivery-price-provider.component';

type TDeliveryTypeFormProps = {
  isStaff: boolean;
  price: number;
};

export const DeliveryTypeForm = ({
  price,
  isStaff = false,
}: TDeliveryTypeFormProps): JSX.Element => {
  const [deliveryTypes, setDeliveryTypes] = useState<Array<ShippingType>>([]);
  const { values, setFieldValue } = useFormikContext<TOfferForm>();
  const { setDelivery } = useDeliveryTypeContext();

  const isRussia = values.country.toLowerCase() === RUSSIA_CODE;
  const isEkaterinburg = values.city.toLowerCase() === EKATERINBURG;
  const isSverdlovskayaOblast =
    values.state.toLowerCase() === SVERDLOVSKAYA_OBLAST;
  const isPriceAboveFreeDeliveryTrigger = price >= FREE_DELIVERY_PRICE_TRIGGER;
  const isFreeDelivery = isPriceAboveFreeDeliveryTrigger && isRussia;

  useEffect(() => {
    void cartService.getDeliveryTypes().then(setDeliveryTypes);
  }, []);

  const availableDeliveries = useMemo(() => {
    let target: Array<ShippingType> = [];

    if (!isRussia) {
      return target;
    }

    if (isStaff) {
      target = target.concat(
        deliveryTypes.filter(
          (item: ShippingType) => item.code === DeliveryTypeCode.FOR_STAFF,
        ),
      );
    }

    if (isFreeDelivery) {
      return target.concat(
        deliveryTypes.filter((item: ShippingType) =>
          [DeliveryTypeCode.FREE, DeliveryTypeCode.FREE_PVZ].includes(
            item.code,
          ),
        ),
      );
    }

    if (isEkaterinburg || isSverdlovskayaOblast) {
      return target.concat(
        deliveryTypes.filter((item: ShippingType) =>
          [DeliveryTypeCode.PVZ, DeliveryTypeCode.COURIER].includes(item.code),
        ),
      );
    }

    return target.concat(
      deliveryTypes.filter((item: ShippingType) =>
        [DeliveryTypeCode.PVZ_RUSSIA, DeliveryTypeCode.COURIER_RUSSIA].includes(
          item.code,
        ),
      ),
    );
  }, [
    isStaff,
    deliveryTypes,
    isFreeDelivery,
    isRussia,
    isEkaterinburg,
    isSverdlovskayaOblast,
  ]);

  useEffect(() => {
    setFieldValue('shippingTypeCode', availableDeliveries[0]?.code);
  }, [availableDeliveries, setFieldValue]);

  useEffect(() => {
    const targetDelivery = availableDeliveries?.find(
      (item: ShippingType) => item.code === values.shippingTypeCode,
    );

    if (targetDelivery) {
      setDelivery(targetDelivery);
    }
  }, [values.shippingTypeCode, availableDeliveries, setDelivery]);

  if (!isRussia) {
    return (
      <>
        <Header weight="600" level={3}>
          Способ доставки
        </Header>
        <Spacer size={20} />
        <div className={styles.notification}>
          <img src="/images/warning.svg" alt="warning" />
          <Typo variant="l">
            После оформления заказа отправим вам письмо. В&nbsp;нём будут
            различные по&nbsp;стоимости и&nbsp;срокам варианты
            доставки&nbsp;&mdash; нужно лишь выбрать подходящий и&nbsp;написать
            об&nbsp;этом в&nbsp;ответ. После этого мы&nbsp;выставим счёт
            на&nbsp;оплату доставки и&nbsp;начнём собирать заказ.
          </Typo>
        </div>
      </>
    );
  }
  return (
    <>
      <Header weight="600" level={3}>
        Способ доставки
      </Header>
      <Spacer size={20} />
      <Typo>
        Доставка по&nbsp;Екатеринбургу и&nbsp;Свердловской области занимает
        в&nbsp;среднем 1-2&nbsp;дня, по&nbsp;России&nbsp;&mdash;
        до&nbsp;7&nbsp;дней.
      </Typo>

      <Spacer size={24} />
      <div className={styles.row}>
        {chunkArray<ShippingType>(availableDeliveries ?? [], 2).map(
          (deliveryTypesPair: Array<ShippingType>) => (
            <Column span={{ s: 12, l: 6 }} key={deliveryTypesPair[0].code}>
              {deliveryTypesPair.map((deliveryType: ShippingType) => (
                <DeliveryTypeCartField
                  key={deliveryType.code}
                  title={deliveryType.name}
                  price={Number(deliveryType.priceValue)}
                  name="shippingTypeCode"
                  value={deliveryType.code}
                />
              ))}
            </Column>
          ),
        )}
      </div>
    </>
  );
};
